
import { useState } from "react";
import type { DailyAnalytics, DailyTotal } from "@/lib/expenses";
import { formatMoney } from "@/lib/format";

// Compact day-by-day spend strip. Tap a bar to read that day; otherwise the
// header shows the month's average per active day.
//
// Uncontrolled (dashboard): taps only preview the day in the header.
// Controlled (transactions page): pass `selectedDay` + `onSelectDay` to drill
// into a day — the selected bar stays lit and taps bubble up to the parent.
export function DailyChart({
  data,
  selectedDay = null,
  onSelectDay,
}: {
  data: DailyAnalytics;
  selectedDay?: number | null;
  onSelectDay?: (d: DailyTotal | null) => void;
}) {
  const { days, currency, busiestDay, avgPerActiveDayCents } = data;
  const [hover, setHover] = useState<DailyTotal | null>(null);

  const max = Math.max(1, ...days.map((d) => d.totalCents));
  const hasSpend = busiestDay != null;
  if (!hasSpend) return null;

  const selected =
    selectedDay != null ? (days.find((d) => d.day === selectedDay) ?? null) : null;
  // Header reads the hovered day, else the drilled-in day.
  const shown = hover ?? selected;

  const handleClick = (d: DailyTotal) => {
    if (onSelectDay) onSelectDay(selected?.day === d.day ? null : d);
    else setHover((a) => (a?.day === d.day ? null : d));
  };

  return (
    <div className="rounded-[1.75rem] bg-card p-4 shadow-sm ring-1 ring-black/[0.05]">
      <div className="flex items-baseline justify-between">
        <p className="text-sm font-semibold">Daily spend</p>
        <p className="text-xs text-muted-foreground tabular-nums">
          {shown ? (
            <>
              <span className="font-semibold text-foreground">
                {formatMoney(shown.totalCents, currency)}
              </span>{" "}
              · day {shown.day}
            </>
          ) : (
            <>avg {formatMoney(avgPerActiveDayCents, currency)}/day</>
          )}
        </p>
      </div>

      <div
        className="mt-3 flex h-12 items-end gap-px"
        onMouseLeave={() => setHover(null)}
      >
        {days.map((d) => {
          const pct =
            d.totalCents > 0
              ? Math.max(8, Math.round((d.totalCents / max) * 100))
              : 0;
          const isHover = hover?.day === d.day;
          const isSelected = selected?.day === d.day;
          const isBusiest = !shown && busiestDay?.day === d.day;
          const highlight = isHover || isSelected || isBusiest;
          return (
            <button
              key={d.day}
              type="button"
              aria-label={`Day ${d.day}: ${formatMoney(d.totalCents, currency)}`}
              onClick={() => handleClick(d)}
              onMouseEnter={() => d.totalCents > 0 && setHover(d)}
              className="group flex h-full flex-1 items-end"
            >
              <span
                className={
                  "w-full rounded-full transition-colors " +
                  (highlight
                    ? "bg-primary"
                    : d.totalCents > 0
                      ? "bg-primary/25 group-hover:bg-primary/45"
                      : "bg-muted")
                }
                style={{ height: `${Math.max(pct, 3)}%` }}
              />
            </button>
          );
        })}
      </div>
    </div>
  );
}
